import { obtenerDescuentoEfectivo, obtenerCuotasOptica, obtenerDescuentoArmazones } from '../api/backend_v2';

export const DESCUENTO_EFECTIVO_DEFAULT = 10;
export const DESCUENTO_ARMAZONES_DEFAULT = 10;
export const OPCIONES_CUOTAS_SISTEMA = [2, 3, 6, 9, 12];

// Marcas de proveedor CACIC con restricciones comerciales especiales (máx 3 cuotas)
export const MARCAS_CACIC = ['rusty', 'vulk', 'maria cher', 'jellvet'];

export const esMarcaCacic = (marca) => {
  if (!marca) return false;
  const m = String(marca).toLowerCase().trim();
  return MARCAS_CACIC.some((c) => m.includes(c));
};

export const esArmazonCacic = (armazon) => {
  if (!armazon) return false;
  if (esMarcaCacic(armazon.marca)) return true;
  if (armazon.proveedor && String(armazon.proveedor).toLowerCase().includes('cacic')) return true;
  return false;
};

export const redondearMiles = (v) => Math.ceil(parseFloat(v) / 1000) * 1000;

export const calcularPrecioEfectivoConDescuento = (precioPublico, descuentoEfectivo = DESCUENTO_EFECTIVO_DEFAULT) => {
  const base = Number.parseFloat(precioPublico);
  if (!Number.isFinite(base) || base <= 0) return '';
  return String(redondearMiles(base * (1 - descuentoEfectivo / 100)));
};

export const cargarDescuentoEfectivoSeguro = async (fallback = DESCUENTO_EFECTIVO_DEFAULT) => {
  try {
    const { descuento_efectivo } = await obtenerDescuentoEfectivo();
    const value = Number.parseFloat(descuento_efectivo);
    return Number.isFinite(value) ? value : fallback;
  } catch (error) {
    console.error('[precios] Error al obtener descuento efectivo de la optica', error);
    return fallback;
  }
};

export const cargarDescuentoArmazonesSeguro = async (fallback = DESCUENTO_ARMAZONES_DEFAULT) => {
  try {
    const { descuento_armazones } = await obtenerDescuentoArmazones();
    const value = Number.parseFloat(descuento_armazones);
    return Number.isFinite(value) ? value : fallback;
  } catch (error) {
    console.error('[precios] Error al obtener descuento de armazones de la optica', error);
    return fallback;
  }
};

/**
 * Encuentra el proveedor correspondiente a un armazón:
 * 1. Por armazon.id_proveedor
 * 2. Por coincidencia de marca en proveedor.marcas
 * 3. Por nombre de proveedor si está seteado en armazon.proveedor
 */
export const encontrarProveedorArmazon = (armazon, proveedores = []) => {
  if (!armazon || !Array.isArray(proveedores) || proveedores.length === 0) return null;

  // 1. Coincidencia por id_proveedor directo
  if (armazon.id_proveedor) {
    const provId = parseInt(armazon.id_proveedor, 10);
    const encontrado = proveedores.find((p) => p.id === provId);
    if (encontrado) return encontrado;
  }

  // 2. Coincidencia por marca en proveedor.marcas
  if (armazon.marca) {
    const marcaArmazonNorm = String(armazon.marca).toLowerCase().trim();
    const encontradoPorMarca = proveedores.find((p) =>
      Array.isArray(p.marcas) &&
      p.marcas.some((m) => {
        const nombreMarca = String(m?.nombre || m).toLowerCase().trim();
        return nombreMarca && (nombreMarca === marcaArmazonNorm || marcaArmazonNorm.includes(nombreMarca));
      })
    );
    if (encontradoPorMarca) return encontradoPorMarca;
  }

  // 3. Coincidencia por nombre de proveedor en armazon.proveedor
  if (armazon.proveedor) {
    const provNombreNorm = String(armazon.proveedor).toLowerCase().trim();
    const encontradoPorNombre = proveedores.find((p) =>
      String(p.nombre).toLowerCase().trim() === provNombreNorm
    );
    if (encontradoPorNombre) return encontradoPorNombre;
  }

  return null;
};

/**
 * Obtiene las condiciones comerciales aplicables a un armazón:
 * - descuento: si el proveedor tiene descuento_efectivo personalizado, lo usa. Si no, usa fallbackDescuentoArmazon.
 * - cuotasMaximas: si el proveedor tiene cuotas_maximas, lo usa. Si no, si es marca CACIC conocida (fallback), 3. Si no, null.
 * - tieneCondicionEspecial: true si tiene cuotas limitadas o descuento específico.
 */
export const resolverCondicionesArmazon = (armazon, proveedores = [], fallbackDescuentoArmazon = DESCUENTO_ARMAZONES_DEFAULT) => {
  if (!armazon) {
    return {
      descuento: fallbackDescuentoArmazon,
      cuotasMaximas: null,
      tieneCondicionEspecial: false,
      proveedor: null,
    };
  }

  const prov = encontrarProveedorArmazon(armazon, proveedores);

  let descuento = fallbackDescuentoArmazon;
  let cuotasMaximas = null;
  let tieneCondicionEspecial = false;

  if (prov) {
    if (prov.descuento_efectivo != null && !Number.isNaN(Number(prov.descuento_efectivo))) {
      descuento = Number(prov.descuento_efectivo);
      tieneCondicionEspecial = true;
    }
    if (prov.cuotas_maximas != null && !Number.isNaN(Number(prov.cuotas_maximas))) {
      cuotasMaximas = Number(prov.cuotas_maximas);
      tieneCondicionEspecial = true;
    }
  }

  // Fallback de retrocompatibilidad si todavía no se asignó proveedor en la base
  if (cuotasMaximas === null && esArmazonCacic(armazon)) {
    cuotasMaximas = 3;
    tieneCondicionEspecial = true;
  }

  return {
    descuento,
    cuotasMaximas,
    tieneCondicionEspecial,
    proveedor: prov,
  };
};

/**
 * Filtra las cuotas según el tope máximo de cuotas sin interés permitido.
 */
export const filtrarCuotasConTope = (cuotasArray = [], topeCuotas = null) => {
  if (!Array.isArray(cuotasArray) || cuotasArray.length === 0) return [];
  if (topeCuotas == null || !Number.isFinite(topeCuotas)) return cuotasArray;
  const acotadas = cuotasArray.filter((c) => Number(c) <= topeCuotas);
  if (acotadas.length === 0 && cuotasArray.length > 0) {
    return [2, Math.min(3, topeCuotas)];
  }
  return acotadas;
};

/**
 * Filtra las cuotas según si el presupuesto tiene productos de marcas CACIC.
 * Si tiene CACIC, las cuotas máximas permitidas son 3.
 */
export const filtrarCuotasParaPresupuesto = (cuotasArray = [], tieneCacic = false) => {
  return filtrarCuotasConTope(cuotasArray, tieneCacic ? 3 : null);
};

/**
 * Formatea un arreglo de números de cuotas en texto natural.
 * Ej: [2, 3, 6] -> "2, 3 o 6 cuotas sin interés"
 * Ej: [2, 3] -> "2 o 3 cuotas sin interés"
 * Ej: [6] -> "6 cuotas sin interés"
 */
export const formatearTextoCuotas = (cuotasArray = []) => {
  if (!Array.isArray(cuotasArray) || cuotasArray.length === 0) return '';
  const ordenadas = Array.from(new Set(cuotasArray.map(Number).filter(n => Number.isFinite(n) && n > 0))).sort((a, b) => a - b);
  if (ordenadas.length === 0) return '';
  if (ordenadas.length === 1) {
    return `${ordenadas[0]} cuotas sin interés`;
  }
  const ult = ordenadas[ordenadas.length - 1];
  const previas = ordenadas.slice(0, -1).join(', ');
  return `${previas} o ${ult} cuotas sin interés`;
};

export const cargarCuotasOpticaSeguro = async () => {
  try {
    const res = await obtenerCuotasOptica();
    const disponibles = Array.isArray(res?.cuotas_disponibles)
      ? res.cuotas_disponibles
      : [];
    return {
      cuotas_disponibles: disponibles,
      cuotas_sin_interes: res?.cuotas_sin_interes || (disponibles.length ? Math.max(...disponibles) : null),
    };
  } catch (error) {
    console.error('[precios] Error al obtener cuotas de la optica', error);
    return {
      cuotas_disponibles: [],
      cuotas_sin_interes: null,
    };
  }
};

